import React from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import styled from '@emotion/styled';

import useUserContext from '../../hooks/use-user-context';
import { createOptionMap } from '../../utilities/misc';
import { rem } from '../../utilities/style';
import { colors } from '../../theme/colors';
import { containerStyles } from '../../styles/layout';
import { fontWeights } from '../../theme/type';

import chineseLeagalIcon from '../../images/chinese-legal.png';
import FooterMenu from './FooterMenu';
import FooterUtilityMenu from './FooterUtilityMenu';
import FooterQr from './FooterQr';
import TextRenderer from '../TextRenderer';
import Link from '../Link';
import { ReactComponent as IconArrowRight } from '../../images/icons/arrow-right.svg';
import { media } from '../../theme/media';
// import CnPromo from './CnPromo';

const ArrowStyled = styled(IconArrowRight)`
  display: inline-block;
  color: ${colors.blue['500']};
  margin-left: ${rem(8)};
  transition: transform 200ms ease;
`;

const Container = styled.footer`
  font-size: ${rem(14)};
  background:${colors.white};
  color: ${colors.blue['800']};

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    background-color: ${colors.blue['800']};
  `}
`;

const ContentWrapper = styled.div`
  ${containerStyles.regular}
  padding-top: ${rem(56)};
`;

const MenuContainer = styled.div`
  display: flex;
  flex-direction: column-reverse;
  gap: ${rem(40)} ${rem(30)};
  padding-top: ${rem(48)};
  border-top: 1px solid ${colors.gray['200']};
  margin-bottom: ${rem(48)};

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    border-color: ${colors.gray['700']};
  `}

  ${media('>smedium')} {
    flex-direction: row;
  }
`;

const FooterMenuStyled = styled(FooterMenu)`
  flex-grow: 1;
`;

const Callout = styled(Link)`
  display: block;
  align-self: start;
  font-size: ${rem(20)};
  font-weight: ${fontWeights.bold};
  line-height: 1.33;
  color: ${colors.blue['800']};

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    color: ${colors.white};
  `}

  ${media('>smedium')} {
    flex: 0 1 ${rem(220)};
  }

  &:hover ${ArrowStyled} {
    transform: translateX(8px);
  }
`;

const MoreInfo = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding: ${rem(20)} 0 ${rem(32)} 0;
  border-top: 1px solid ${colors.gray['200']};

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    border-top-color: ${colors.gray['700']};
  `}
`;

const FooterSubcontent = styled.div`
  flex: 1 1 50%;
  margin-right: auto;
`;

const FooterUtilityMenuStyled = styled(FooterUtilityMenu)`
  padding-top: ${rem(30)};
`;

const Notice = styled.div`
  padding: ${rem(20)} 0px;
  display: flex;
  flex-wrap: wrap;
  gap: ${rem(8)} ${rem(20)};
  color: ${colors.gray['600']};

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
   color: ${colors.gray['400']};
  `}
`;

const ChineseLegal = styled.div`
  display: flex;
`;

const ChineseLegalIcon = styled.img`
  display:inline-block;
  margin-right: ${rem(5)};
`;

// todo Code is duplicated from FooterMenu.js and would be better to import
const MenuLink = styled(Link)`
  font-size: ${rem(14)};
  color: ${colors.gray['600']};
  transition: color .2s ease;

  &:hover {
    text-decoration: underline;
    color: ${colors.blue['800']};
  }

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    color: ${colors.gray['400']};
    &:hover {
      color: ${colors.blue['100']};
    }
  `}
`;

const year = new Date().getFullYear();

const Footer = () => {
  const data = useStaticQuery(graphql`
    query ChineseFooter {
      allContentfulOptionSet(filter: {name: {regex: "/^(globals|footer)$/"}}) {
        nodes {
          ...OptionSetFragment
        }
      }
    }
  `);
  const { language } = useUserContext();
  const options = createOptionMap(data.allContentfulOptionSet.nodes, language);
  const copyrightNotice = options['footer.copyright_notice']
    ?.replace('{{year}}', year);
  const calloutText = options['footer.chinese_callout_text'];
  const calloutLink = options['footer.chinese_callout_link'];

  return (
    <Container>
      <ContentWrapper>
        <MenuContainer>
          <FooterMenuStyled />
          <Callout to={calloutLink}>
            <TextRenderer text={calloutText} />
            <ArrowStyled />
          </Callout>
        </MenuContainer>
        <MoreInfo>
          <FooterSubcontent>
            <FooterUtilityMenuStyled />
            <Notice>
              <TextRenderer text={copyrightNotice} />
              <ChineseLegal>
                <ChineseLegalIcon
                  src={chineseLeagalIcon}
                  alt=""
                  role="presentation"
                  aria-hidden="true"
                />
                <MenuLink to="http://www.beian.gov.cn/portal/registerSystemInfo?recordcode=33010602011788">浙公网安备 33010602011788号</MenuLink>
                &nbsp;
                <MenuLink to="https://beian.miit.gov.cn">浙ICP备17034078号-3</MenuLink>
              </ChineseLegal>
            </Notice>
          </FooterSubcontent>
          <FooterQr />
        </MoreInfo>
      </ContentWrapper>
    </Container>
  );
};

export default Footer;
